/** USD threshold aligned with backend KYC_THRESHOLD_USD (shopper basic KYC). */
export const KYC_THRESHOLD_USD = 3000;

/**
 * Resolve rkfl token + merchantAuth from postMessage cart and/or order-details payload.
 */
export function resolveCartAuthFields(cartMessage = {}, hostedPageInfo = {}) {
  const hostedCustomer = hostedPageInfo?.customerInfo || {};
  const messageCustomer = cartMessage?.customerInfo || {};

  const token =
    cartMessage?.token ??
    cartMessage?.rkflToken ??
    messageCustomer?.rkflToken ??
    hostedCustomer?.rkflToken;

  const merchantAuth =
    cartMessage?.merchantAuth ??
    messageCustomer?.merchantAuth ??
    hostedCustomer?.merchantAuth;

  const customerInfo = {
    ...hostedCustomer,
    ...messageCustomer,
    ...(token != null ? { rkflToken: token } : {}),
    ...(merchantAuth != null ? { merchantAuth } : {}),
  };

  return { token, merchantAuth, customerInfo };
}

function isPresent(value) {
  return value != null && value !== "";
}

/**
 * Mirror hosted-page.vue setCartData — top-level token/merchantAuth for autologin.
 * Old postMessage handler used event.data.data.token directly.
 */
export function prepareIframeCartFromPostMessage(data = {}) {
  const cart = { ...data };
  const rkflToken =
    data?.token ??
    data?.rkflToken ??
    data?.customerInfo?.rkflToken;
  const merchantAuth =
    data?.merchantAuth ?? data?.customerInfo?.merchantAuth;

  if (isPresent(rkflToken)) {
    cart.token = rkflToken;
  }
  if (isPresent(merchantAuth)) {
    cart.merchantAuth = merchantAuth;
  }
  if (isPresent(rkflToken) || isPresent(merchantAuth)) {
    cart.customerInfo = {
      ...(data.customerInfo || {}),
      ...(isPresent(rkflToken) ? { rkflToken } : {}),
      ...(isPresent(merchantAuth) ? { merchantAuth } : {}),
    };
  }
  return cart;
}

/** Ensure cart has top-level token/merchantAuth + customerInfo.rkflToken for iframe auth. */
export function withResolvedCartAuth(
  cart = {},
  rawMessage = {},
  hostedPageInfo = null
) {
  const { token, merchantAuth, customerInfo } = resolveCartAuthFields(
    { ...rawMessage, ...cart },
    hostedPageInfo || cart
  );
  const { token: _token, merchantAuth: _merchantAuth, ...rest } = cart;

  return {
    ...rest,
    customerInfo,
    ...(isPresent(token) ? { token } : {}),
    ...(isPresent(merchantAuth) ? { merchantAuth } : {}),
  };
}

/** Credentials for iframe autologin after order-details merge. */
export function getIframeAutoLoginCredentials(cartPayload = {}, rawMessage = {}) {
  const { token, merchantAuth } = resolveCartAuthFields(
    { ...rawMessage, ...cartPayload },
    cartPayload
  );
  const rkflToken = isPresent(token) ? token : null;
  const auth = isPresent(merchantAuth) ? merchantAuth : null;
  return {
    rkflToken,
    merchantAuth: auth,
    canAutoLogin: Boolean(auth && rkflToken),
  };
}

/**
 * Merge postMessage cart fields with hosted order-details payload.
 */
export function mergeIframeCartPayload(messageCart, hostedPageInfo) {
  if (!hostedPageInfo || typeof hostedPageInfo !== "object") {
    return messageCart;
  }

  const { token, merchantAuth, customerInfo } = resolveCartAuthFields(
    messageCart,
    hostedPageInfo
  );

  return {
    ...hostedPageInfo,
    ...messageCart,
    customerInfo,
    uuid: messageCart?.uuid || hostedPageInfo.uuid,
    merchant_id: messageCart?.merchant_id ?? hostedPageInfo.merchant_id,
    amount: messageCart?.amount ?? hostedPageInfo.amount,
    currency: messageCart?.currency ?? hostedPageInfo.currency,
    order: messageCart?.order ?? hostedPageInfo.order,
    cart: messageCart?.cart ?? hostedPageInfo.cart,
    encrypted: messageCart?.encrypted ?? hostedPageInfo.encrypted,
    amountUsd: hostedPageInfo.amountUsd ?? messageCart?.amountUsd,
    kycRequired: hostedPageInfo.kycRequired ?? messageCart?.kycRequired,
    token,
    merchantAuth,
    redirectUrl: messageCart?.redirectUrl ?? hostedPageInfo.redirectUrl,
    enableGlobalIframeRedirect:
      messageCart?.enableGlobalIframeRedirect ??
      hostedPageInfo.enableGlobalIframeRedirect,
    enableMerchantIframeRedirect:
      messageCart?.enableMerchantIframeRedirect ??
      hostedPageInfo.enableMerchantIframeRedirect,
  };
}

/**
 * Re-apply KYC fields from order-details after the shopper is authenticated.
 */
export function applyOrderKycFields(cart, hostedPageInfo) {
  if (!cart || !hostedPageInfo) {
    return cart;
  }
  return {
    ...cart,
    amountUsd: hostedPageInfo.amountUsd ?? cart.amountUsd,
    kycRequired: hostedPageInfo.kycRequired ?? cart.kycRequired,
  };
}

/**
 * Whether the shopper must complete KYC before paying.
 */
export function resolveKycRequired(cart) {
  if (!cart || typeof cart !== "object") {
    return false;
  }
  if (cart.kycRequired != null) {
    return Boolean(cart.kycRequired);
  }
  const amountUsd = cart.amountUsd ?? cart.amount;
  if (amountUsd == null || amountUsd === "") {
    return false;
  }
  return Number(amountUsd) >= KYC_THRESHOLD_USD;
}
